<template>
  <FriedBottomBar
    ref="promptContainer"
    :class="[
      'action-bottom-bar-container',
      { 'hide-expanded': hasReachedBreakpoint(), 'set-min-width': hasSetMinWidth },
    ]"
    :style="`left: ${promptPosition};`"
  >
    <slot name="icon" />
    <div
      :class="['content-container', { nowrap: noWrapText }]"
      data-external="bottom-bar-content-container"
    >
      <slot name="content" />
    </div>
    <div :class="['actions-container', getBreakpointClass('down')]"><slot name="actions" /></div>
    <div :class="['expand-button-container', getBreakpointClass('up')]">
      <FriedIconButton data-external="action-bottom-bar-expand-button" @click="toggleExpand">
        <FriedIcon icon="chevron-down" :class="['expand-button-icon', { expanded }]" />
      </FriedIconButton>
    </div>
    <div v-if="expanded" :class="['actions-container', getBreakpointClass('up')]">
      <slot name="actions" />
    </div>
  </FriedBottomBar>
</template>

<script lang="ts">
import type { PropType, Ref } from 'vue';
import { defineComponent, ref, onMounted, onBeforeUnmount } from 'vue';
import { isWindowSize } from '../helpers/window-size.helper';
import { ScreenSize, WindowSize } from '@getaccept/lib-shared/src/enums/window-size';

export default defineComponent({
  props: {
    referenceContainer: {
      type: HTMLElement as PropType<HTMLElement>,
      default: () => document.body,
    },
    breakpoint: {
      type: Number as PropType<WindowSize>,
      default: WindowSize.XSmall,
    },
    hasSetMinWidth: {
      type: Boolean,
      default: true,
    },
    noWrapText: {
      type: Boolean,
    },
  },
  setup(props) {
    const promptContainer: Ref<HTMLElement> = ref(null);
    const expanded = ref(false);
    const promptPosition = ref('50%');

    const hasReachedBreakpoint = () => isWindowSize(props.breakpoint);

    const getBreakpointClass = (direction: 'up' | 'down') => {
      let breakpoint = '';
      switch (props.breakpoint) {
        case WindowSize.XSmall:
          breakpoint = ScreenSize.Xs;
          break;
        case WindowSize.Small:
          breakpoint = ScreenSize.Sm;
          break;
        case WindowSize.Medium:
          breakpoint = ScreenSize.Md;
          break;
        case WindowSize.Large:
          breakpoint = ScreenSize.Lg;
          break;
      }
      return `hidden-${breakpoint}-and-${direction}`;
    };

    const toggleExpand = () => (expanded.value = !expanded.value);

    const updatePrompt = () => {
      hasReachedBreakpoint();

      if (props.referenceContainer !== document.body) {
        window.requestAnimationFrame(() => {
          const { x, width } = props.referenceContainer.getBoundingClientRect();
          promptPosition.value = `${x + width / 2}px`;
        });
      }
    };

    onMounted(() => {
      updatePrompt();
      window.addEventListener('resize', updatePrompt);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updatePrompt);
    });

    return {
      promptContainer,
      toggleExpand,
      hasReachedBreakpoint,
      promptPosition,
      getBreakpointClass,
      expanded,
      updatePrompt,
    };
  },
});
</script>

<style lang="scss" scoped>
.action-bottom-bar-container {
  max-width: 95%;

  &.set-min-width {
    width: 700px;
  }

  .actions-container {
    display: flex;
    align-items: center;
  }

  .content-container {
    padding: 0 var(--spacing-50);
    max-width: 70%;
    flex: 1;

    &.nowrap {
      white-space: nowrap;
    }
  }

  &.hide-expanded {
    flex-wrap: wrap;
    transform: translate(-50%, calc(var(--spacing-50) * -1));
    width: 95%;
    text-align: center;

    .actions-container {
      margin: auto;
      width: 100%;
      justify-content: center;
    }

    .expand-button-icon {
      transition: transform 0.3s;

      &.expanded {
        transform: rotate(-180deg);
      }
    }
  }
}
</style>
